import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment/min/moment-with-locales";
import {
  GetBusinessMonthlyStatementAPI,
  type MonthlyStatementResult,
} from "@/services/api/business/getMonthlyStatement";
import { GetBusinessCreatedCouponsAPI } from "@/services/api/coupon/getBusinessCreatedCoupons";
import { GetSponsorsByUserAPI } from "@/services/api/sponsor/getSponsorsByUser";
import {
  GetBusinessDashboardStatsAPI,
  type BusinessDashboardStats,
} from "@/services/api/business/getBusinessDashboardStats";
import { computeStatementTotals } from "@/utils/businessStatement";

/**
 * Dashboard data for the business portal: the selected month's statement
 * (gross sales, fees, per-coupon breakdown), the portfolio counters used
 * on the summary cards, and the month's reach & engagement stats.
 */
export function useBusinessDashboard(month: moment.Moment) {
  const business = useSelector((state: any) => state.auth.business);

  const [statement, setStatement] = useState<MonthlyStatementResult | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const [couponCount, setCouponCount] = useState(0);
  const [sponsorCount, setSponsorCount] = useState(0);

  const [reach, setReach] = useState<BusinessDashboardStats | null>(null);
  const [reachLoading, setReachLoading] = useState(true);

  const loadStatement = useCallback(
    async (refreshing = false) => {
      if (refreshing) setRefreshing(true);
      const resp = await GetBusinessMonthlyStatementAPI({
        year: month.format("YYYY"),
        month: month.format("M"),
      });
      if (resp?.status === 0) {
        setStatement(resp.result ?? null);
        setError("");
      } else {
        setStatement(null);
        setError(String(resp?.result?.message ?? resp?.message ?? ""));
      }
      setLoading(false);
      setRefreshing(false);
    },
    [month],
  );

  useEffect(() => {
    loadStatement();
  }, [loadStatement]);

  const loadReach = useCallback(
    async (refreshing = false) => {
      if (!refreshing) setReachLoading(true);
      const resp = await GetBusinessDashboardStatsAPI({
        year: month.format("YYYY"),
        month: month.format("M"),
      });
      if (resp?.status === 0) {
        setReach(resp.result ?? null);
      } else {
        setReach(null);
      }
      setReachLoading(false);
    },
    [month],
  );

  useEffect(() => {
    loadReach();
  }, [loadReach]);

  const loadCounters = useCallback(async () => {
    const [couponResp, sponsorResp] = await Promise.all([
      GetBusinessCreatedCouponsAPI({ page: 1 }),
      GetSponsorsByUserAPI({ page: 1 }),
    ]);

    const coupons = Array.isArray(couponResp?.result)
      ? (couponResp.result as unknown[])
      : [];
    setCouponCount(coupons.length);
    setSponsorCount(
      sponsorResp?.status === 0 ? (sponsorResp.result?.total ?? 0) : 0,
    );
  }, []);

  useEffect(() => {
    loadCounters();
  }, [loadCounters]);

  const totals = useMemo(
    () => computeStatementTotals(statement, month, business?.ctime),
    [business?.ctime, month, statement],
  );

  const items = useMemo(() => {
    const list = statement?.items ?? statement?.records ?? [];
    return Array.isArray(list) ? list : [];
  }, [statement]);

  return {
    statement,
    items,
    totals,
    loading,
    refreshing,
    error,
    couponCount,
    sponsorCount,
    reach,
    reachLoading,
    refresh: () => {
      loadStatement(true);
      loadReach(true);
    },
  };
}
